import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import {
  fetchCampaignDetails,
  fetchCampaignStatusData,
  unwrapCampaignStatusPayload,
} from "../../api/campaigns.api";
import { businessPath } from "../../config/businessMenu";

const STATUS_META = {
  submitted: { label: "Submitted", description: "All recipients included in this campaign." },
  sent: { label: "Sent", description: "Recipients whose message was sent to WhatsApp." },
  delivered: { label: "Delivered", description: "Recipients whose message was delivered." },
  read: { label: "Read", description: "Recipients who read the message." },
  failed: { label: "Failed", description: "Recipients whose message failed to send." },
  replied: { label: "Replied", description: "Recipients who replied after the campaign message." },
  payments: { label: "Payments", description: "Payment orders collected for this campaign." },
};

const formatAmount = (amount, currency = "INR") => {
  if (amount === null || amount === undefined || amount === "") return "—";
  if (currency === "INR") return `₹ ${amount}`;
  if (currency === "USD") return `$ ${amount}`;
  return `${amount} ${currency}`;
};

const mapPaymentRow = (row, index) => ({
  _id: row?._id || row?.id || `payment-${index}`,
  reference_id: row?.reference_id || row?.orderRef || row?.order_ref || "",
  name: row?.name || row?.customer_name || "",
  waid: row?.waid || row?.wa_id || row?.waId || row?.recipient_id || "",
  total_amount: row?.total_amount ?? row?.amount ?? null,
  currency: row?.currency || "INR",
  gateway: row?.gateway || "",
  status: row?.status || "",
  createdAt: row?.createdAt || row?.created_at || null,
});

const formatPhone = (value) => {
  if (!value) return "—";
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return String(value);
  return digits.startsWith("+") ? digits : `+${digits}`;
};

const formatWhen = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
};

const errorText = (details) => {
  if (!details) return "—";
  if (typeof details === "string") return details;
  if (Array.isArray(details)) {
    return details
      .map((item) => item?.message || item?.title || item?.error || "")
      .filter(Boolean)
      .join("; ") || "—";
  }
  return details.message || details.title || details.error || "—";
};

const CampaignStatusDetail = () => {
  const { projectId, campaignId, status } = useParams();
  const statusKey = String(status || "").toLowerCase();
  const meta = STATUS_META[statusKey];

  const [campaignName, setCampaignName] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / limit) || 1);

  const loadRows = useCallback(async () => {
    if (!campaignId || !meta) return;
    setLoading(true);
    setError("");
    try {
      if (statusKey === "payments") {
        const campaign = await fetchCampaignDetails(campaignId);
        setCampaignName(campaign?.campaignName || "");
        const payments = Array.isArray(campaign?.payments) ? campaign.payments : [];
        const mapped = payments.map(mapPaymentRow);
        const start = (page - 1) * limit;
        setTotal(mapped.length);
        setRows(mapped.slice(start, start + limit));
        return;
      }
      const res = await fetchCampaignStatusData(campaignId, statusKey, { page, limit });
      const payload = unwrapCampaignStatusPayload(res);
      setRows(payload.data);
      setTotal(Number(payload.total) || payload.data.length);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(
        err.message ||
          (statusKey === "payments"
            ? "Failed to load campaign payments."
            : "Failed to load campaign recipients."),
      );
    } finally {
      setLoading(false);
    }
  }, [campaignId, limit, meta, page, statusKey]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!campaignId) return;
    fetchCampaignDetails(campaignId)
      .then((campaign) => setCampaignName(campaign?.campaignName || ""))
      .catch(() => setCampaignName(""));
  }, [campaignId]);

  useEffect(() => {
    setPage(1);
  }, [statusKey]);

  const showErrorColumn = statusKey === "failed";
  const isPayments = statusKey === "payments";

  const columns = useMemo(() => {
    if (isPayments) {
      return ["#", "Reference ID", "Name", "WA ID", "Amount", "Status", "Created"];
    }
    const cols = ["#", "Name", "Phone", "Status", "Updated"];
    if (showErrorColumn) cols.push("Error");
    return cols;
  }, [isPayments, showErrorColumn]);

  if (!meta) {
    return <Navigate to={businessPath(projectId, `campaign/${campaignId}`)} replace />;
  }

  return (
    <div className="campaign-detail-page campaign-status-page">
      <header className="campaign-detail-topbar">
        <div className="campaign-detail-topbar-left">
          <Link
            to={businessPath(projectId, `campaign/${campaignId}`)}
            className="campaign-detail-back-icon"
            aria-label="Back to campaign"
          >
            <FaArrowLeft />
          </Link>
          <div>
            <h1>
              {meta.label}
              {campaignName ? ` · ${campaignName}` : ""}
            </h1>
            <p className="campaign-status-subtitle">{meta.description}</p>
          </div>
        </div>
      </header>

      {error && <p className="campaign-detail-error">{error}</p>}

      <section className="campaign-status-table-section">
        <p className="campaign-status-count">
          {loading
            ? isPayments
              ? "Loading payments…"
              : "Loading recipients…"
            : `${total} ${isPayments ? "payment" : "recipient"}${total === 1 ? "" : "s"}`}
        </p>
        <div className="biz-table-wrap">
          <table className="biz-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columns.length} className="biz-table-empty">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="biz-table-empty">
                    {isPayments
                      ? "No payments in this section."
                      : "No recipients in this section."}
                  </td>
                </tr>
              )}
              {!loading &&
                !isPayments &&
                rows.map((row, index) => (
                  <tr key={row._id || `${row.recipient_id}-${index}`}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{row.name || "—"}</td>
                    <td className="biz-table-mono">
                      {formatPhone(row.recipient_id || row.recipient_waid)}
                    </td>
                    <td className="campaign-status-pill-cell">
                      <span className={`campaign-status-row-pill campaign-status-row-${String(row.status || "").toLowerCase()}`}>
                        {row.status || "—"}
                      </span>
                    </td>
                    <td>{formatWhen(row.updatedAt || row.createdAt)}</td>
                    {showErrorColumn && <td>{errorText(row.errordetails)}</td>}
                  </tr>
                ))}
              {!loading &&
                isPayments &&
                rows.map((row, index) => (
                  <tr key={row._id || `payment-${index}`}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td className="biz-table-mono">{row.reference_id || "—"}</td>
                    <td>{row.name || "—"}</td>
                    <td className="biz-table-mono">{formatPhone(row.waid)}</td>
                    <td>{formatAmount(row.total_amount, row.currency)}</td>
                    <td className="campaign-status-pill-cell">
                      <span className={`campaign-status-row-pill campaign-status-row-${String(row.status || "").toLowerCase()}`}>
                        {row.status || "—"}
                      </span>
                    </td>
                    <td>{formatWhen(row.createdAt)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="biz-pagination">
          <div>
            <label htmlFor="campaign-status-page-size">Show:</label>{" "}
            <select
              id="campaign-status-page-size"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
          <div className="biz-pagination-right">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CampaignStatusDetail;
